/** Shared polling interval for server-backed lists (TanStack Query). */
export const REFETCH_INTERVAL_MS = 20_000;

/** Snappier polling interval when items in the list are actively queued or processing. */
export const ACTIVE_PROCESSING_REFETCH_INTERVAL_MS = 5_000;

/** Interval for awaiting a single file's processor result (GetFileInfo loops). */
export const FILE_PROCESS_POLL_INTERVAL_MS = 2500;

/** Max GetFileInfo polls before timing out (~5 minutes at FILE_PROCESS_POLL_INTERVAL_MS). */
export const FILE_PROCESS_MAX_ATTEMPTS = 120;

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** NavBar / customer filter sentinel — "all customers". */
export const ALL_CUSTOMERS_OPTION = { userCompanyId: EMPTY_GUID, companyContact: 'ALL' };

/** PlanTable / estimate forms — no customer selected. */
export const NA_CUSTOMER_OPTION = { userCompanyId: EMPTY_GUID, companyContact: 'N/A' };

/** Estimate forms — no reference plan selected. */
export const NA_FILE_OPTION = { fileId: EMPTY_GUID, fileName: 'N/A' };

/** Options list with N/A sentinel first so Autocomplete value always matches an option. */
export function customerAutocompleteOptions(customers) {
    const list = Array.isArray(customers)
        ? customers.filter((c) => c?.userCompanyId !== EMPTY_GUID)
        : [];
    return [NA_CUSTOMER_OPTION, ...list];
}

export function isCustomerOptionEqual(option, value) {
    return option?.userCompanyId === value?.userCompanyId;
}

/** Same pattern for reference-file Autocompletes that use NA_FILE_OPTION. */
export function fileAutocompleteOptions(files) {
    const seen = new Set();
    const list = [];
    for (const file of Array.isArray(files) ? files : []) {
        const id = file?.fileId;
        if (!id || id === EMPTY_GUID || seen.has(id)) {
            continue;
        }
        seen.add(id);
        list.push(file);
    }
    return [NA_FILE_OPTION, ...list];
}

export function isFileOptionEqual(option, value) {
    return option?.fileId === value?.fileId;
}

/** Returns true if any row in data is currently queued or in-progress. */
export function hasActiveProcessingItems(data) {
    if (!data) {
        return false;
    }
    const items = Array.isArray(data)
        ? data
        : (data?.filesDTO ?? data?.invoicesDTO ?? data?.items ?? data?.data);
    if (!Array.isArray(items)) {
        return false;
    }
    return items.some((item) => {
        if (!item) {
            return false;
        }
        // 0 = Queued, 3 = RevalidationQueued
        return item.isProcessed === 0 || item.isProcessed === 3;
    });
}

/** Dynamic polling interval that drops to 5s when plans/invoices are processing, pausing when hidden. */
export function refetchIntervalWithActiveProcessing(data) {
    if (typeof document === 'undefined' || !document.visibilityState) {
        return REFETCH_INTERVAL_MS;
    }
    if (document.visibilityState !== 'visible') {
        return false;
    }
    return hasActiveProcessingItems(data)
        ? ACTIVE_PROCESSING_REFETCH_INTERVAL_MS
        : REFETCH_INTERVAL_MS;
}

/** Pause background polling when the browser tab is hidden. */
export function refetchIntervalWhenTabVisible() {
    if (typeof document === 'undefined' || !document.visibilityState) {
        return REFETCH_INTERVAL_MS;
    }
    return document.visibilityState === 'visible' ? REFETCH_INTERVAL_MS : false;
}
